import type { FlatConfig } from './flatConfig';
import { FLAT_CONFIG_KEYS } from './flatConfig';
import type { SystemConfiguration } from './SystemConfiguration';
import { systemConfigFromFlat, systemConfigToFlat } from './SystemConfiguration';
import type { Pricing } from './Pricing';
import { pricingFromFlat, pricingToFlat } from './Pricing';
import type { QuoteMetadata } from './QuoteMetadata';
import { quoteMetadataFromFlat, quoteMetadataToFlat } from './QuoteMetadata';
import type { ViewState } from './ViewState';
import { viewStateFromFlat, viewStateToFlat } from './ViewState';
import { SPARES_OPTIONS } from '../constants/storageConstants';
import { CURRENT_SCHEMA_VERSION, SCHEMA_MIGRATIONS } from './schemaVersion';

export { CURRENT_SCHEMA_VERSION };

const KNOWN_FLAT_KEYS: ReadonlySet<string> = new Set(FLAT_CONFIG_KEYS);

export interface DomainConfiguration {
  schemaVersion: number;
  system: SystemConfiguration;
  pricing: Pricing;
  metadata: QuoteMetadata;
  viewState: ViewState;
  // Flat fields no box recognizes (typically added on main after this model
  // was written). Carried verbatim so a round trip never silently drops data;
  // a non-empty bag is a signal that flatConfig.ts needs a new field.
  unknownFields: Record<string, unknown>;
}

export function fromFlatConfig(c: FlatConfig): DomainConfiguration {
  const unknownFields: Record<string, unknown> = {};
  const raw = c as unknown as Record<string, unknown>;
  for (const key of Object.keys(raw)) {
    if (!KNOWN_FLAT_KEYS.has(key)) {
      unknownFields[key] = raw[key];
    }
  }
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    system: systemConfigFromFlat(c),
    pricing: pricingFromFlat(c),
    metadata: quoteMetadataFromFlat(c),
    viewState: viewStateFromFlat(c),
    unknownFields,
  };
}

// No `as FlatConfig` cast: each box mapper now returns a precise Pick of the keys
// it owns, so the object literal's type must structurally cover all of FlatConfig.
// If a FlatConfig field is added but no box maps it, this stops compiling.
export function toFlatConfig(m: DomainConfiguration): FlatConfig {
  return {
    // Unknown passthrough goes first so the known boxes below always win a
    // key collision (a stale unknownFields entry can never shadow real state).
    ...m.unknownFields,
    ...systemConfigToFlat(m.system),
    ...pricingToFlat(m.pricing),
    ...quoteMetadataToFlat(m.metadata),
    ...viewStateToFlat(m.viewState),
    availableSpares: [...SPARES_OPTIONS], // copy: don't alias the shared constant
  };
}

// Read seam for persisted configs: upgrade an older-versioned raw config to the
// current schema by applying registered migrations in FROM-version order. Throws
// on an unknown or newer version so bad data fails loudly rather than silently.
export function migrateToCurrentSchema(
  raw: Record<string, unknown> & { schemaVersion?: number },
): DomainConfiguration {
  let version = typeof raw.schemaVersion === 'number' ? raw.schemaVersion : 1;
  let data: Record<string, unknown> = raw;
  while (version < CURRENT_SCHEMA_VERSION) {
    const migration = SCHEMA_MIGRATIONS[version];
    if (!migration) {
      throw new Error(`No schema migration registered from version ${version}`);
    }
    data = migration(data);
    version += 1;
  }
  if (version !== CURRENT_SCHEMA_VERSION) {
    throw new Error(
      `Unsupported schemaVersion ${version} (current is ${CURRENT_SCHEMA_VERSION})`,
    );
  }
  return data as unknown as DomainConfiguration;
}
